import {
  ODSAvatar,
  ODSColumn,
  ODSSideNavigation,
  type ODSSideNavigationItems,
} from '@telekom-ods/react-ui-kit';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import type keycloak from 'src/keycloak';
import { useUserData } from 'src/redux/selectors';

import useAppContext from '../../hooks/useAppContext';
import useLayoutPermissions from '../AppLayout/hooks/useLayoutPermissions';
import useSideNavItems from '../AppLayout/hooks/useSideNavItems';

import BottomCollapsedItem from './BottomCollapsedItem';
import NavItem from './NavItem';
import SideNavigationHeader from './SideNavigationHeader';

interface SideNavigationMainProps {
  authenticated?: boolean;
  idToken: string | undefined;
  keycloak: typeof keycloak;
}

const SideNavigationMain = ({ authenticated, keycloak }: SideNavigationMainProps) => {
  const [expanded, setExpanded] = useState(true);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const isAuthenticated = authenticated;
  const { toggleTheme, theme, selectedOrganization } = useAppContext();

  const userState = useUserData();
  const { organization, roles } = userState;

  const currentOrganization = selectedOrganization ?? organization;
  const permissions = useLayoutPermissions(roles ?? [], currentOrganization);
  const items = useSideNavItems({
    intl,
    permissions,
    navigate,
    pathname,
    selectedOrganization,
    ownOrganizationData: organization ?? null,
    isAuthenticated,
  });

  return (
    <ODSSideNavigation
      className='side-navigation-main'
      defaultExpanded
      expanded={expanded}
      closeSideNavButtonProps={{
        onClick: () => {
          setExpanded(false);
        },
      }}
      headerSlot={<SideNavigationHeader theme={theme} />}
      bottomBarCollapsedSlot={
        <ODSColumn>
          {authenticated && (
            <NavItem
              {...{
                to: `/docs/${import.meta.env.VITE_CONFLUENCE_HOMEPAGE}`,
                theme,
                label: 'Docs',
                icon: 'link-type-standard',
                collasped: true,
              }}
            />
          )}
          <NavItem
            {...{
              to: '/support-and-feedback',
              theme,
              label: 'Help',
              icon: 'help-type-standard-size-small',
              collasped: true,
            }}
          />
          <ODSAvatar
            icon='light-dark-mode-type-bold'
            name='theme-toggle'
            showBackground
            size='large'
            variant='icon'
            onClick={toggleTheme}
          />
        </ODSColumn>
      }
      bottomBarExpandedSlot={
        <BottomCollapsedItem
          {...(authenticated && { authenticated })}
          keycloak={keycloak}
        />
      }
      items={items as ODSSideNavigationItems[]}
      onExpandedChanged={expand => {
        setExpanded(expand);
      }}
    />
  );
};

export default SideNavigationMain;
